import type {
  AssigneeSummary,
  CustomerSentiment,
  SupportTicketListItemResponse,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import {
  ConversationAssigneeSummary as SupportTicketAssigneeSummary,
  ConversationResolutionSentiment as SupportTicketResolutionSentiment,
} from "@redotech/redo-model/conversation-insights";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import {
  ConversationAssigneeSummaryColorMap as SupportTicketAssigneeSummaryColorMap,
  ConversationAssigneeSummaryLabels as SupportTicketAssigneeSummaryLabels,
  ConversationResolutionSentimentColorMap as SupportTicketResolutionSentimentColorMap,
  ConversationResolutionSentimentLabels as SupportTicketResolutionSentimentLabels,
} from "@redotech/redo-web/conversation-insights/conversation-insights-ui";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useQuery } from "@tanstack/react-query";
import { memo } from "react";
import { supportTicketDetailBundleQueryOptions } from "../db/support-ticket-data-source";
import { CollapsibleSection } from "./collapsible-section";
import { FieldLabel } from "./field-label";

export interface AiInsightsSectionProps {
  supportTicket: SupportTicketListItemResponse;
}

const SentimentBadge = memo(function SentimentBadge({
  sentiment,
}: {
  sentiment: CustomerSentiment;
}) {
  const sentimentEnum =
    sentiment as unknown as SupportTicketResolutionSentiment;
  return (
    <RedoBadge
      color={SupportTicketResolutionSentimentColorMap[sentimentEnum]}
      size="sm"
      text={SupportTicketResolutionSentimentLabels[sentimentEnum]}
    />
  );
});

const AssigneeSummaryBadge = memo(function AssigneeSummaryBadge({
  assigneeSummary,
}: {
  assigneeSummary: AssigneeSummary;
}) {
  const assigneeSummaryEnum =
    assigneeSummary as unknown as SupportTicketAssigneeSummary;
  return (
    <RedoBadge
      color={SupportTicketAssigneeSummaryColorMap[assigneeSummaryEnum]}
      size="sm"
      text={SupportTicketAssigneeSummaryLabels[assigneeSummaryEnum]}
    />
  );
});

const formatTimestamp = (date: Date | null): string => {
  if (!date) {
    return "-";
  }
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export const AiInsightsSection = memo(function AiInsightsSection({
  supportTicket,
}: AiInsightsSectionProps) {
  const effectApi = useRequiredContext(EffectApiClientContext);

  const { data: summaryData } = useQuery({
    ...supportTicketDetailBundleQueryOptions(effectApi, supportTicket.id),
    select: (bundle) => bundle.summary,
  });

  const realSummary = summaryData?.summary ?? null;

  const hasSummaryData =
    realSummary ||
    supportTicket.customerSentiment ||
    supportTicket.assigneeSummary ||
    supportTicket.lastCustomerMessageAt ||
    supportTicket.lastMerchantMessageAt;

  if (!hasSummaryData) {
    return null;
  }

  return (
    <CollapsibleSection
      localStorageKey="redo.support-v2.support-ticket-details.summary.collapsed"
      title="Support ticket"
    >
      {realSummary && (
        <Flex dir="column" gap="xs">
          <FieldLabel>Summary</FieldLabel>
          <Text fontSize="xs" textColor="primary">
            {realSummary}
          </Text>
        </Flex>
      )}

      {supportTicket.customerSentiment && (
        <Flex align="center" gap="md" justify="space-between">
          <FieldLabel>Sentiment</FieldLabel>
          <SentimentBadge sentiment={supportTicket.customerSentiment} />
        </Flex>
      )}

      {supportTicket.assigneeSummary && (
        <Flex align="center" gap="md" justify="space-between">
          <FieldLabel>Assignee</FieldLabel>
          <AssigneeSummaryBadge
            assigneeSummary={supportTicket.assigneeSummary}
          />
        </Flex>
      )}

      <Flex align="flex-start" gap="md" justify="space-between">
        <FieldLabel>Last customer message</FieldLabel>
        <Text fontSize="xs" textColor="primary" whiteSpace="nowrap">
          {formatTimestamp(supportTicket.lastCustomerMessageAt)}
        </Text>
      </Flex>

      <Flex align="flex-start" gap="md" justify="space-between">
        <FieldLabel>Last merchant message</FieldLabel>
        <Text fontSize="xs" textColor="primary" whiteSpace="nowrap">
          {formatTimestamp(supportTicket.lastMerchantMessageAt)}
        </Text>
      </Flex>
    </CollapsibleSection>
  );
});
